import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';

  const [otp, setOtp] = useState(['', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verified, setVerified] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Start resend cooldown on mount
  useEffect(() => {
    startCooldown();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  function startCooldown() {
    setResendCooldown(60);
  }

  function handleChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;
    e.preventDefault();
    const next = [...otp];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, 3)]?.focus();
  }

  async function handleVerify() {
    const code = otp.join('');
    if (code.length < 4) return;
    setVerifying(true);
    try {
      const res = await api.post('/api/v1/auth/verify-email', { email, otp_code: code });
      // Backend wraps a bad/expired OTP in an HTTP 200 body with success:false,
      // so axios won't throw — surface the error instead of the success screen.
      if (res?.success === false) {
        toast.error(res.message || 'Invalid or expired code.');
        setOtp(['', '', '', '']);
        inputRefs.current[0]?.focus();
        return;
      }
      setVerified(true);
      toast.success('Email verified! You can now log in.');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Invalid or expired code.';
      toast.error(msg);
      setOtp(['', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || !email) return;
    setResending(true);
    try {
      const res = await api.post('/api/v1/auth/resend-verification', { email });
      if (res?.success === false) {
        toast.error(res.message || 'Failed to resend code.');
        return;
      }
      toast.success('Verification code resent. Check your inbox.');
      startCooldown();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to resend code.');
    } finally {
      setResending(false);
    }
  }

  if (verified) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/10 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1 text-center pt-4 pb-2">
            <div className="mx-auto -my-4">
              <img src="/logo-color.svg" alt="QuBox" className="h-56 w-auto" />
            </div>
            <div className="space-y-3 pt-2">
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
              <CardTitle className="text-2xl font-bold">Email Verified</CardTitle>
              <CardDescription className="text-base">
                Your email has been verified. You can now log in.
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/10 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center pt-4 pb-2">
          <div className="mx-auto -my-4">
            <img src="/logo-color.svg" alt="QuBox" className="h-56 w-auto" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">Check your email</CardTitle>
            <CardDescription className="text-base mt-2">
              We sent a 4-digit code to
              {email && <strong className="block text-foreground mt-1">{email}</strong>}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* OTP input boxes — always left-to-right, even in RTL layouts */}
          <div dir="ltr" className="flex justify-center gap-2" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                dir="ltr"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="h-12 w-12 rounded-lg border border-input bg-background text-center text-lg font-semibold focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            ))}
          </div>

          {/* Verify button */}
          <Button
            className="w-full"
            disabled={otp.join('').length < 4 || verifying}
            onClick={handleVerify}
          >
            {verifying ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
            Verify Email
          </Button>

          {/* Resend */}
          <p className="text-center text-sm text-muted-foreground">
            Didn't receive it?{' '}
            {resendCooldown > 0 ? (
              <span className="text-muted-foreground">Resend in {resendCooldown}s</span>
            ) : (
              <button
                className="font-medium text-primary hover:underline disabled:opacity-50"
                disabled={resending}
                onClick={handleResend}
              >
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            )}
          </p>

          <div className="text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3 w-3" /> Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
